'use client';

import { useTranslations } from 'next-intl';
import { useStyles } from '@/shared/lib/hooks/useStyles';
import Image from 'next/image';
import { useSubscriptionLimits } from '@/features/subscription';
import { SettingsCard, PickerRow } from '@/shared/ui';
import { Alert, AlertDescription } from '@/shared/ui/shadcn/alert';
import { Skeleton } from '@/shared/ui/shadcn/skeleton';
import { ReferenceSourceGroup } from './ReferenceSourceGroup';

export interface StyleContentProps {
  selectedStyleIds: number[] | null;
  onStyleChange: (styleIds: number[] | null) => void;
  // Project reference props (optional)
  projectId?: string;
  styleProjectReferenceIds?: string[] | null;
  onStyleProjectReferencesChange?: (ids: string[] | null) => void;
  // Auto refs toggle (for Auto mode)
  useAutoStyleRefs?: boolean;
  onUseAutoStyleRefsChange?: (value: boolean) => void;
  // Layout variant — kept for API parity; both variants now use the same
  // Apple-grouped row idiom (light-gray card + thumbnail rows).
  variant?: 'default' | 'compact';
  // Navigate to references management
  onNavigateToReferences?: () => void;
}

export function StyleContent({
  selectedStyleIds,
  onStyleChange,
  projectId,
  styleProjectReferenceIds,
  onStyleProjectReferencesChange,
  useAutoStyleRefs = false,
  onUseAutoStyleRefsChange,
  onNavigateToReferences,
}: StyleContentProps) {
  const t = useTranslations('render.settings.style');
  const { groupedStyles, isLoading, error } = useStyles();
  const limits = useSubscriptionLimits();
  const maxReferences = limits?.simultaneous_references || 3;

  const hasSelectedRefs = (styleProjectReferenceIds?.length ?? 0) > 0;
  const isAtLimit = (selectedStyleIds || []).length >= 2;

  const handleStyleToggle = (styleId: number) => {
    const currentIds = [...(selectedStyleIds || [])];

    // Picking a library style is mutually exclusive with project references:
    // clear any picked refs and drop the reference source to "No references".
    if (hasSelectedRefs) {
      onStyleProjectReferencesChange?.(null);
    }
    onUseAutoStyleRefsChange?.(false);

    if (currentIds.includes(styleId)) {
      const newIds = currentIds.filter((id) => id !== styleId);
      onStyleChange(newIds.length === 0 ? null : newIds);
    } else if (currentIds.length < 2) {
      const newIds = [...currentIds, styleId].sort((a, b) => a - b);
      onStyleChange(newIds);
    }
  };

  const handleSelectNone = () => {
    onUseAutoStyleRefsChange?.(false);
    onStyleProjectReferencesChange?.(null);
  };

  const handleSelectAll = () => {
    onStyleChange(null);
    onStyleProjectReferencesChange?.(null);
    onUseAutoStyleRefsChange?.(true);
  };

  const handleSelectiveChange = (ids: string[]) => {
    if (ids.length > 0) {
      onStyleChange(null);
    }
    onStyleProjectReferencesChange?.(ids.length > 0 ? ids : null);
  };

  if (isLoading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-12 w-full rounded-2xl" />
        <Skeleton className="h-44 w-full rounded-2xl" />
      </div>
    );
  }

  if (error) {
    return (
      <Alert variant="destructive">
        <AlertDescription>{t('loadError', { error })}</AlertDescription>
      </Alert>
    );
  }

  const renderStyleSection = (
    styles: typeof groupedStyles.contemporary,
    title: string
  ) => {
    if (styles.length === 0) return null;
    return (
      <SettingsCard title={title}>
        {styles.map((style) => {
          const styleId = parseInt(style.id);
          const isSelected = (selectedStyleIds || []).includes(styleId);
          const isDisabled = !isSelected && isAtLimit;
          const imageUrl = style.attributes.image_url;

          // Apple `SelectableImageRow` thumbnail — 44×32 with rounded corners
          // and a muted placeholder. iOS/macOS uses CachedImage; web mirrors
          // with next/image inside a fixed frame.
          const leading = (
            <div className="w-11 h-8 rounded bg-muted overflow-hidden relative">
              {imageUrl && (
                <Image
                  src={imageUrl}
                  alt={style.attributes.name}
                  fill
                  sizes="44px"
                  className="object-cover invert dark:invert-0"
                />
              )}
            </div>
          );

          return (
            <PickerRow
              key={style.id}
              title={style.attributes.name}
              leading={leading}
              isSelected={isSelected}
              isDisabled={isDisabled}
              onClick={() => handleStyleToggle(styleId)}
            />
          );
        })}
      </SettingsCard>
    );
  };

  return (
    <div className="space-y-4">
      {/* Reference source: No references / All project references /
          Selective references (one always selected). */}
      {projectId && onStyleProjectReferencesChange && (
        <ReferenceSourceGroup
          projectId={projectId}
          category="style"
          selectedIds={styleProjectReferenceIds || []}
          useAllRefs={useAutoStyleRefs}
          maxSelection={maxReferences}
          onSelectNone={handleSelectNone}
          onSelectAll={handleSelectAll}
          onSelectiveChange={handleSelectiveChange}
          onNavigateToReferences={onNavigateToReferences}
        />
      )}

      {/* Modern styles group */}
      {renderStyleSection(groupedStyles.contemporary, t('modern'))}

      {/* Traditional styles group */}
      {renderStyleSection(groupedStyles.traditional, t('traditional'))}
    </div>
  );
}

